import type { FinancialRepresentative } from '../../../../services/personApi';
import { formatCpf } from '../../../../utils/taxIdUtils';
import type { SearchablePickerOption } from '../../../organisms/SearchablePicker';

const toIndividualUuid = (item: FinancialRepresentative): string => item.individualUuid;

const toCandidateOption = (candidate: FinancialRepresentative): SearchablePickerOption => ({
    id: candidate.individualUuid,
    label: candidate.name,
    hint: formatCpf(candidate.socialId),
});

/**
 * Adds a Financial Representative at the end of the list; a person already in it is not added again.
 */
export const addFinancialRepresentative = (
    list: FinancialRepresentative[],
    item: FinancialRepresentative,
): FinancialRepresentative[] => {
    if (list.some((current) => current.individualUuid === item.individualUuid)) return list;
    return [...list, item];
};

/**
 * Removes only the Financial Representative of the given individual.
 */
export const removeFinancialRepresentative = (
    list: FinancialRepresentative[],
    individualUuid: string,
): FinancialRepresentative[] => list.filter((item) => item.individualUuid !== individualUuid);

/**
 * The individual ids sent as financialRepresentativeIds in PUT /legal-persons/{id}.
 */
export const toFinancialRepresentativeIds = (list: FinancialRepresentative[]): string[] => list.map(toIndividualUuid);

/**
 * True when both lists hold the same individuals, in any order.
 */
export const sameFinancialRepresentativeIds = (a: string[], b: string[]): boolean => {
    const left = new Set(a);
    const right = new Set(b);
    if (left.size !== right.size) return false;
    return Array.from(left).every((id) => right.has(id));
};

/**
 * Search results offered by the picker, without the people already in the list.
 */
export const toCandidateOptions = (
    candidates: FinancialRepresentative[],
    selected: FinancialRepresentative[],
): SearchablePickerOption[] => {
    const selectedIds = new Set(selected.map(toIndividualUuid));
    return candidates
        .filter((candidate) => !selectedIds.has(candidate.individualUuid))
        .map(toCandidateOption);
};
